import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
// We no longer need OTP functions here
import { checkUserStatus, resetPasswordApi } from "../api/getApi";

const ResetPassword = () => {
  const navigate = useNavigate();
  // Add 'success' to the possible steps
  const [step, setStep] = useState("enterMobile"); // 'enterMobile', 'enterPassword', 'migrate', 'notFound', 'success'
  const [mobile, setMobile] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const getErrorMessageString = (err, defaultMessage) => {
    return err?.response?.data?.error || err?.message || defaultMessage;
  };

  // Step 1: Check if the mobile number is registered
  const handleCheckMobile = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^[6-9]\d{9}$/.test(mobile)) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await checkUserStatus(mobile);
      // Set the step based on the user's status
      if (response.status === "password_required") {
        setStep("enterPassword");
      } else if (response.status === "migration_required") {
        setStep("migrate");
      } else { // 'not_found'
        setStep("notFound");
      }
      setError(""); // Clear any previous errors
    } catch (err) {
      setError(getErrorMessageString(err, "Could not verify mobile number."));
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Set the new password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(newPassword)) {
       setError("Password must include uppercase, lowercase, number, and a special character.");
       return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await resetPasswordApi(mobile, newPassword);
      if (response && response.success) {
        // On success, set a message and change the step to show the success view
        setSuccessMessage("Your password has been reset successfully.");
        setError("");
        setStep("success");
      } else {
        setError(response?.error || "Failed to reset password.");
      }
    } catch (err) {
      setError(getErrorMessageString(err, "An error occurred while resetting your password."));
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    switch (step) {
      case "enterMobile":
        return (
          <form onSubmit={handleCheckMobile} className="space-y-4">
            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700">
                Registered Mobile Number
              </label>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                required
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className={`mt-1 w-full px-4 py-2.5 border rounded-lg ${error ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Enter your 10-digit mobile number"
                disabled={isLoading}
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center bg-blue-600 text-white font-semibold py-2.5 px-4 rounded-lg"
              >
                {isLoading ? "Verifying..." : "Verify Mobile Number"}
              </button>
            </div>
          </form>
        );
      case "enterPassword":
        return (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Mobile Number: <span className="font-medium text-gray-800">{mobile}</span></p>
            </div>
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`mt-1 w-full px-4 py-2.5 border rounded-lg ${error.includes('Password') || error.includes('match') ? 'border-red-500' : 'border-gray-300'}`}
                disabled={isLoading}
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                 className={`mt-1 w-full px-4 py-2.5 border rounded-lg ${error.includes('match') ? 'border-red-500' : 'border-gray-300'}`}
                disabled={isLoading}
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center bg-green-600 text-white font-semibold py-2.5 px-4 rounded-lg"
              >
                {isLoading ? "Resetting..." : "Reset Password"}
              </button>
            </div>
          </form>
        );
      case "migrate":
        return (
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Account Update Required</h3>
            <p className="text-sm text-gray-600">
              Welcome back! To reset your password, you first need to complete our one-time security update by creating a new password.
            </p>
            <p className="mt-3 font-medium text-sm text-gray-700">
              Your saved data will be migrated automatically.
            </p>
            <button
              onClick={() => navigate("/signup")}
              className="mt-6 w-full flex justify-center items-center bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold py-3 px-4 rounded-lg"
            >
              Proceed to Sign Up
            </button>
          </div>
        );
      case "notFound":
        return (
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-800 mb-3">Account Not Found</h3>
            <p className="text-sm text-gray-600">
              No account is associated with this mobile number. Please create an account to continue.
            </p>
            <button
              onClick={() => navigate("/signup")}
              className="mt-6 w-full flex justify-center items-center bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold py-3 px-4 rounded-lg"
            >
              Create an Account
            </button>
          </div>
        );
      case "success":
        return (
          <div className="text-center">
            <h3 className="text-xl font-bold text-green-600 mb-3">Success!</h3>
            <p className="text-sm text-gray-600">
              {successMessage}
            </p>
            <button
              onClick={() => navigate("/login")}
              className="mt-6 w-full flex justify-center items-center bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold py-3 px-4 rounded-lg"
            >
              Proceed to Login
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-800">Reset Password</h2>
          </div>

          <div className="p-6">
            {error && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-md" role="alert">
                <p className="text-sm">{error}</p>
              </div>
            )}
            {successMessage && step !== 'success' && (
              <div className="mb-4 bg-green-50 border-l-4 border-green-500 text-green-700 p-4 rounded-md" role="alert">
                <p className="text-sm">{successMessage}</p>
              </div>
            )}

            {renderContent()}

            {/* Hide the bottom link on the success screen */}
            {step !== 'success' && (
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Remember your password?{" "}
                  <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                    Sign in
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;